<script setup lang="ts">
import { useId } from 'vue'

withDefaults(
  defineProps<{
    /** The section heading, in medium weight and primary ink; omit for a section that is only content. */
    title?: string
    headingLevel?: 'h2' | 'h3'
    /** Space between the head and each content block: `default` 8px · `loose` 24px, for a bare top block. */
    gap?: 'default' | 'loose'
  }>(),
  { title: undefined, headingLevel: 'h2', gap: 'default' },
)

defineSlots<{
  /** The section's content: panels, a metric row, a bare top block. */
  default(): unknown
  /** A quiet caption that belongs to the title, such as a count or a state, set right after it. */
  meta?: () => unknown
  /** Controls at the end of the head row; one `ghost` AppButton hangs 8px into the edge so its label lines up with the content. */
  actions?: () => unknown
  /** A caption-size line under the content, inset 4px. */
  foot?: () => unknown
}>()

const headingId = useId()
</script>

<template>
  <section
    :aria-labelledby="title ? headingId : undefined"
    class="flex min-w-0 flex-col"
    :class="gap === 'loose' ? 'gap-12' : 'gap-4'"
  >
    <header
      v-if="title || $slots.meta || $slots.actions"
      class="flex min-h-(--control-height-sm) items-center justify-between gap-8"
    >
      <div v-if="title || $slots.meta" class="flex min-w-0 items-baseline gap-4">
        <component
          :is="headingLevel"
          v-if="title"
          :id="headingId"
          class="min-w-0 text-body-sm-medium text-text-primary"
        >
          {{ title }}
        </component>
        <span
          v-if="$slots.meta"
          class="shrink-0 whitespace-nowrap text-caption tabular-nums text-text-tertiary"
        >
          <slot name="meta" />
        </span>
      </div>
      <div v-if="$slots.actions" class="-me-4 ml-auto flex shrink-0 items-center gap-4">
        <slot name="actions" />
      </div>
    </header>
    <slot />
    <div v-if="$slots.foot" class="px-2 text-caption text-text-tertiary">
      <slot name="foot" />
    </div>
  </section>
</template>
